// @flow weak

import Router from 'next/router';
import getConfig from 'next/config';
import { endsWith, get, partial } from 'lodash';

import { isBrowser, isProduction } from '@zapier/common-utils';

import type { RouterProps } from 'web-ssr/app/common/next/types';

const nextConfig = getConfig();

const CDN_ASSET_PREFIX =
  get(nextConfig, ['publicRuntimeConfig', 'CDN_ASSET_PREFIX']) || '';

// Used to clean any state we are injecting for the client, e.g. Apollo
export const cleanState = state => {
  return JSON.stringify(state).replace(/</g, '\\u003c');
};

// Helper function for Next.js routes, if we are looking at the root, it will
// be represented as `/path/name/index.js`, rather than `path/name/.js`
export const getPagePathname = pathName => {
  if (endsWith(pathName, '/')) {
    return `${pathName}index.js`;
  }

  return `${pathName}.js`;
};

// When we are in production, we will serve static assets over a CDN. This
// helper will assist us in building those URLs.
export const buildAbsolutePath = url => {
  const prefix = isProduction() ? `${CDN_ASSET_PREFIX}/_next` : '/';
  return `${prefix}/${url}`;
};

// Used for static assets, e.g. images, anything stored under <root_dir>/static
export const buildStaticAssetPath = url => {
  const prefix = isProduction()
    ? `${CDN_ASSET_PREFIX}/_next/static`
    : `/_next/static`;

  return `${prefix}/${url}`;
};

export const findWebpackChunk = (assetMap, path, resourceType = 'css') => {
  const result = get(assetMap, [path, resourceType], null);

  // Ensure we return an absolute link reference
  return result ? buildAbsolutePath(result) : null;
};

export const doesListContainUrl = (urls, urlToMatch) =>
  urls.some(url => endsWith(url, urlToMatch));

export const getPageParams = (
  param: string,
  routerProps: RouterProps,
  defaultValue?: any
) => get(routerProps, ['query', param], defaultValue);

export const getSelectedTabSlug = partial(getPageParams, 'tab');
export const getSelectedAppSlug = partial(getPageParams, 'slug');
export const getBlogSlug = partial(getPageParams, 'blogSlug');
export const getPrimaryAppSlug = partial(getPageParams, 'primarySlug');
export const getSecondaryAppSlug = partial(getPageParams, 'secondarySlug');
export const getZapId = partial(getPageParams, 'zapId');
export const getZapSlug = partial(getPageParams, 'zapSlug');

export const redirectClient = (context, target, statusCode = 301) => {
  if (context.res) {
    // On the server-side we need to redirect the response
    context.res.redirect(statusCode, target);
  } else {
    // On the client-side we can leverage the Router
    Router.replace(target);
  }
};

// Canoical URLs need to be sorted alphabetically
export const getCanonicalOrdering = (items: string[]) => {
  return items.sort((a, b) => a.localeCompare(b));
};

export const stripHtmlTagsFromString = (htmlString: string) => {
  let str = '';

  try {
    if (isBrowser()) {
      const div = document.createElement('div');
      div.innerHTML = htmlString;

      str = div.textContent || div.innerText || '';
    } else {
      // This module should only appear on the server-side, it is ignored on the
      // client-side in `next.config.js` using webpacks `IgnorePlugin`
      // See https://arunoda.me/blog/ssr-and-server-only-modules for more info
      const sanitizeHtml = require('striptags');

      str = sanitizeHtml(htmlString);
    }
  } catch (e) {
    return str;
  }

  // Replace new-lines with a space and trim
  return str.replace(/\r?\n|\r/g, ' ').trim();
};

export const is3xxStatus = (status?: number) =>
  status && status > 299 && status < 400;
export const is4xxStatus = (status?: number) =>
  status && status > 399 && status < 500;
export const is5xxStatus = (status?: number) =>
  status && status > 499 && status < 600;
